/* eslint-disable max-lines-per-function */
import {
  ChartAPIReturn,
  ChartAssetData,
  Colors,
  CustomMixDomain,
  defaultColors,
  isIntradayResample,
  PaneData,
  RangeHorizon,
  ResampleIntervals,
  SeriesType,
  TimeSeriesItem,
  YAxisType,
} from '@toggle/chart';
import { isSinglePriceEntity } from '@toggle/helpers';
import { Entity, Resolution, SnakeMeta } from '@toggle/toggle';
import { useEffect, useRef, useState } from 'react';

import { postEntities } from '~/api/entities/entity-service';
import { getPrices } from '~/api/price/price-service';
import { fetchSnakeByName } from '~/services/overview-widget/overview-widget-service';
import { Tracking } from '~/services/tracking';
import { useChartLoad } from '~/views/turbo-chart/hooks/use-chart-load/useChartLoad';
import { ChartSearchType } from '~/views/turbo-chart/types/search.types';

import { ChartSettings } from '../../utils/chart-settings-utils.ts/chart-settings-utils';
import {
  createChartDataWithChangedAsset,
  createTsDataFromSnake,
  getHorizons,
  getSimilarPane,
  getSnakeMeta,
  paneContainsAsset,
  updateChartPane,
  updatePane,
} from '../../utils/chart-utils/chart-utils';
import {
  getAssetEmptyResamples,
  getAssetResample,
  getChartDataEmptyResamples,
} from '../../utils/resample-utils/resample-utils';
import { useChartErrors } from '../use-chart-errors/useChartErrors';
import { useChartPane } from '../use-chart-pane/useChartPane';
import { useChartSettings } from '../use-chart-settings/useChartSettings';
import { ChartState, useChartState } from '../use-chart-state/useChartState';
import { useResample } from '../use-resample/useResample';

export interface GetTimeSeriesProps {
  latestAsset: Entity;
  resolution: Resolution;
  dateFrom?: string;
  dateTo?: string;
}

export interface OnChartDataReadyProps {
  assetHorizons: ReturnType<typeof getHorizons>;
  chartPanes: PaneData[];
  emptyResamples: ResampleIntervals[];
  validResample: ResampleIntervals;
  validHorizon: RangeHorizon | undefined;
  domain: CustomMixDomain | null;
  hidden: string[] | undefined;
  dailyTimeseries: TimeSeriesItem[];
}

export interface UseTurboChartProps {
  openSearch?: (
    type: ChartSearchType,
    {
      changingAssets,
      paneIndex,
    }: { changingAssets?: Entity; paneIndex?: number }
  ) => void;
  initialChartSettings?: ChartSettings;
  initialChartState?: ChartState;
  shouldUseSearchParams?: boolean;
  colors?: Colors;
}

export type UseTurboChartReturn = ReturnType<typeof useTurboChart>;

export const useTurboChart = ({
  openSearch,
  initialChartSettings,
  initialChartState,
  shouldUseSearchParams = true,
  colors = defaultColors,
}: UseTurboChartProps) => {
  const {
    chartState,
    updateChartState,
    resetChartState,
    primaryAsset,
    primaryPane,
    setChartState,
  } = useChartState(shouldUseSearchParams);
  const [dailyTimeseries, setDailyTimeseries] = useState<TimeSeriesItem[]>();
  const [isLoading, setIsLoading] = useState(false);
  const chartPanes = chartState.chartPanes;

  const chartData = chartPanes.flatMap(panes => panes.chartAssetData);
  const activeAssets = chartData.map(c => c.entity);
  const activeAssetsPerPanels = chartPanes.map(pane =>
    pane.chartAssetData.map(c => ({ entity: c.entity, snakeMeta: c.snakeMeta }))
  );

  const hasSinglePriceEntity = activeAssets.some(isSinglePriceEntity);

  const [horizonRanges, setHorizonRanges] = useState<RangeHorizon[]>(() =>
    Object.values(RangeHorizon)
  );
  const chartApiRef = useRef<null | ChartAPIReturn>(null);

  const { chartError } = useChartErrors({ chartData });

  const onChartDataReady = ({
    assetHorizons,
    chartPanes,
    emptyResamples,
    validResample,
    validHorizon,
    dailyTimeseries,
    domain,
    hidden,
  }: OnChartDataReadyProps) => {
    setHorizonRanges(assetHorizons.horizons);
    resample.setEmptyResamples(emptyResamples);
    updateChartState({
      resample: validResample,
      horizon: validHorizon,
      chartPanes,
      domain,
      hidden,
    });
    updateChartData(chartPanes, validResample);
    setDailyTimeseries(dailyTimeseries);
  };

  const getTimeSeries = async ({
    latestAsset,
    dateFrom,
    dateTo,
    resolution,
  }: GetTimeSeriesProps) => {
    try {
      if (isSinglePriceEntity(latestAsset)) {
        const snakeData = await fetchSnakeByName(latestAsset.default_snake);

        if (snakeData.error) {
          throw snakeData.error;
        }

        return createTsDataFromSnake(snakeData.data.result.data);
      } else {
        const prices = await getPrices({
          ticker: latestAsset.subscribable_ticker,
          entityTag: latestAsset.tag,
          resolution: resolution,
          dateFrom,
          dateTo,
        });

        if (prices.data) {
          return prices.data;
        }

        const snakeData = await fetchSnakeByName(latestAsset.default_snake);
        return (
          snakeData.data && createTsDataFromSnake(snakeData.data.result.data)
        );
      }
    } catch (error) {
      Tracking.captureException(error as Error);
      return undefined;
    }
  };

  const { changeYAxisSettings, loadChartFromSettings } = useChartSettings({
    ...chartError,
    getTimeSeries,
    onChartDataReady,
    chartPanes,
    updateChartState,
    colors,
  });

  const { handleInitialChartLoad, retryInitialChartLoad } = useChartLoad({
    initialChartSettings,
    initialChartState,
    openSearch,
    loadChartFromSettings,
    setHasError: chartError.setHasError,
    shouldUseSearchParams,
    setIsLoading,
    updateChartState,
  });

  const { seriesType: selectedSeriesType, yAxisType } = chartPanes[0];

  const {
    changeRange,
    updateChartData,
    onDomainChange,
    disable1DHorizon,
    resample,
  } = useResample({
    ...chartError,
    chartData,
    chartState,
    hasSinglePriceEntity,
    updateChartState,
    getTimeSeries,
    primaryAsset,
    setIsLoading,
  });

  const chartPaneActions = useChartPane({
    ...chartError,
    primaryAsset,
    chartPanes,
    updateChartState,
    resample,
    colors,
  });

  useEffect(() => {
    return () => {
      chartApiRef.current?.reset();
    };
  }, []);

  const onIndicatorSelect = async ({
    indicator,
    asset,
    assetToChange,
  }: {
    indicator: SnakeMeta;
    asset: Entity;
    assetToChange?: Entity;
  }) => {
    let newPaneIndex = getSimilarPane({ chartPanes, assetToChange, indicator });
    const hasChartData = chartPanes[0].chartAssetData.length;

    //if there is no pane with similar snake
    if (newPaneIndex === -1 && hasChartData) {
      await chartPaneActions.addIndicatorPane({
        indicator,
        asset,
        assetToChange,
      });
      return;
    }

    //if there is pane with similar snake
    newPaneIndex = newPaneIndex === -1 ? 0 : newPaneIndex;
    const chartData = chartPanes[newPaneIndex].chartAssetData;

    const [snakeData, dailyData] = await Promise.all([
      fetchSnakeByName(indicator.snake),
      resample.fetchResampleIfNeeded(chartData, ResampleIntervals.OneDay),
    ]);

    if (snakeData.error) {
      chartError.setHasError(true);
      return;
    }

    const prices = createTsDataFromSnake(snakeData.data.result.data);
    if (!prices?.length) {
      chartError.setAssetWithError(asset);
      chartError.handleEmptyChart(asset.name);
      return;
    }

    const newChartData = createChartDataWithChangedAsset({
      primaryAsset,
      chartData: dailyData,
      asset: {
        ...asset,
        default_snake: indicator.snake,
        currency: indicator.currency,
      },
      snakeMeta: indicator,
      prices,
      assetToChange,
      colors,
    });

    const newChartPanes: PaneData[] = updatePane(chartPanes, newPaneIndex, {
      chartAssetData: newChartData,
    });

    resample.setEmptyResamples(
      getAssetEmptyResamples(
        primaryAsset?.entity ?? newChartData[0].entity,
        true
      )
    );

    const optionalHorizon = isIntradayResample(resample.selectedResample)
      ? { horizon: RangeHorizon.ThreeMonths }
      : { horizon: chartState.horizon ?? RangeHorizon.ThreeMonths };
    updateChartState({
      chartPanes: newChartPanes,
      resample: ResampleIntervals.OneDay,
      ...optionalHorizon,
    });
  };

  const onAssetChange = async ({
    searchedAsset,
    assetToChange,
  }: {
    searchedAsset: Entity;
    assetToChange?: Entity;
  }) => {
    // TODO This can be removed once data is updated on BE. This is added to fix missing name_short values in economic indicator entities
    // The data will be updated but until then this additional call is made to merge the tag with the default_snake and name
    // https://app.clickup.com/t/8693kpd3t
    const entities = await postEntities({
      args: [searchedAsset.tag],
      filter: 2,
    });
    if (entities.error) {
      chartError.setAssetWithError(searchedAsset);
      chartError.handleEmptyChart(searchedAsset.name);
      return;
    }
    const asset: Entity = {
      ...entities.data[0],
      default_snake: searchedAsset.default_snake,
      name: searchedAsset.name,
    };
    chartError.setHasEmpty(false);
    chartError.setHasError(false);
    const isSinglePrice = isSinglePriceEntity(asset);
    const nextResample = getAssetResample({
      asset,
      hasSinglePriceEntity: isSinglePrice,
      selectedResample: resample.selectedResample,
    });

    const [prices, snakeMeta] = await Promise.all([
      getTimeSeries({
        latestAsset: asset,
        resolution: nextResample,
      }),
      getSnakeMeta(asset.default_snake),
    ]);

    if (!prices?.length) {
      chartError.setAssetWithError(asset);
      chartError.handleEmptyChart(asset.name);
      return;
    }
    const assetIndex = chartPanes.findIndex(p =>
      paneContainsAsset(
        p,
        assetToChange
          ? { entity: assetToChange }
          : (primaryAsset as ChartAssetData)
      )
    );

    const primaryAssetIndex = assetIndex === -1 ? 0 : assetIndex;

    let horizon,
      seriesType = selectedSeriesType;

    const paneIndex = assetToChange
      ? chartPanes.findIndex(p =>
          paneContainsAsset(p, { entity: assetToChange })
        )
      : -1;
    const newChartData = createChartDataWithChangedAsset({
      chartData: chartPanes[primaryAssetIndex].chartAssetData,
      asset,
      prices,
      snakeMeta,
      assetToChange,
      primaryAsset,
      colors,
    });

    const primaryPaneYAxisType: YAxisType =
      newChartData.length > 1 ? 'split' : yAxisType;
    let newChartPanes: PaneData[] = [
      ...chartPanes.slice(0, primaryAssetIndex),
      {
        ...chartPanes[primaryAssetIndex],
        yAxisType: primaryPaneYAxisType,
        seriesType,
        chartAssetData: newChartData,
      },
      ...chartPanes.slice(primaryAssetIndex + 1, chartPanes.length),
    ];

    if (assetToChange) {
      const chartData = newChartPanes[paneIndex].chartAssetData.filter(
        c => c.entity.default_snake !== assetToChange.default_snake
      );

      if (chartData.length) {
        newChartPanes[paneIndex].chartAssetData = chartData;
      } else {
        newChartPanes.splice(paneIndex, 1);
      }
    }

    resample.setEmptyResamples(getChartDataEmptyResamples(newChartData));
    if (!chartData.length) {
      const assetHorizons = getHorizons();
      horizon = assetHorizons.activeHorizon;
      seriesType =
        isSinglePrice || !selectedSeriesType ? 'line' : selectedSeriesType;
      setHorizonRanges(assetHorizons.horizons);
    }
    const optionalHorizon = horizon ? { horizon } : {};

    updateChartData(newChartPanes, nextResample);
    updateChartState({
      chartPanes: newChartPanes,
      resample: nextResample,
      ...optionalHorizon,
    });
  };

  const removeActiveAsset = (defaultSnake: string, paneIndex = 0) => {
    const chartAssetPane = chartPanes[paneIndex];
    const filteredData = chartAssetPane.chartAssetData.filter(
      a => a.entity.default_snake !== defaultSnake
    );
    const yAxisType: YAxisType = filteredData.length === 1 ? 'merged' : 'split';

    const newChartPanes = updatePane(chartPanes, paneIndex, {
      chartAssetData: filteredData,
      yAxisType,
    });

    updateChartData(newChartPanes);
    updateChartState({
      chartPanes: newChartPanes,
    });
    resample.setEmptyResamples(getChartDataEmptyResamples(filteredData));
  };

  const hideActiveAsset = async (
    snake: Entity['default_snake'],
    { isHidden = false, paneIndex = 0 }
  ) => {
    const updatedData = chartPanes[paneIndex].chartAssetData.map(a => {
      if (a.entity.default_snake === snake) {
        return { ...a, isHidden };
      }
      return a;
    });

    const hidden = updatedData
      .filter(item => item.isHidden)
      .map(item => item.entity.default_snake);
    const newChartPanes = updateChartPane(chartPanes, updatedData, paneIndex);
    updateChartData(newChartPanes);
    updateChartState({
      chartPanes: newChartPanes,
      hidden: hidden.length ? hidden : undefined,
    });
  };

  const changeChartLineColor = (
    colorId: number,
    { snake, paneIndex }: { snake?: string; paneIndex: number }
  ) => {
    const updatedChartPanes = chartPanes[paneIndex].chartAssetData.map(a => {
      if (a.entity.default_snake === snake) {
        return { ...a, lineColorOverride: colors.multi[colorId] };
      }
      return a;
    });

    const newChartPanes = updateChartPane(
      chartPanes,
      updatedChartPanes,
      paneIndex
    );
    updateChartData(newChartPanes);
    updateChartState({ chartPanes: newChartPanes });
  };

  const changeSeriesType = (
    seriesType: SeriesType,
    { snake, paneIndex = 0 }: { snake?: string; paneIndex?: number }
  ) => {
    const updatedChartPane: PaneData = snake
      ? {
          ...chartPanes[paneIndex],
          chartAssetData: chartPanes[paneIndex].chartAssetData.map(a => {
            if (a.entity.default_snake === snake) {
              return { ...a, seriesType };
            }
            return a;
          }),
        }
      : { ...chartPanes[0], seriesType };

    updateChartState({
      chartPanes: [
        ...chartPanes.slice(0, paneIndex),
        updatedChartPane,
        ...chartPanes.slice(paneIndex + 1),
      ],
    });
  };

  return {
    ...chartError,
    ...resample,
    chartPaneActions,
    chartData,
    activeAssetsPerPanels,
    removeActiveAsset,
    hideActiveAsset,
    changeRange,
    changeChartLineColor,
    horizonRanges,
    changeSeriesType,
    chartApiRef,
    onAssetChange,
    onIndicatorSelect,
    hasSinglePriceEntity,
    disable1DHorizon,
    onDomainChange,
    changeYAxisSettings,
    dailyTimeseries,
    chartState,
    handleInitialChartLoad,
    retryInitialChartLoad,
    chartIsLoading: isLoading,
    resetChartState,
    primaryPane,
    primaryAsset,
    setChartState,
    updateChartState,
  };
};
